/**
 * Device detection hook for iPad and mobile optimization
 */
import { useState, useEffect } from 'react';

export interface DeviceDetection {
  isIPad: boolean;
  isMobile: boolean;
  orientation: 'portrait' | 'landscape';
  touchSupport: boolean;
  /**
   * True if the primary input is a finger (touchscreen / iPad).
   * Pairs with the global `@media (pointer: coarse)` rules in
   * `index.css` to bump icon-button touch targets. The hook also
   * mirrors this to a `hts-touch` class on `<body>` so any component
   * can opt-in via plain CSS (`.hts-touch .foo { ... }`) — useful for
   * hosts / VMs that don't surface `(pointer: coarse)` reliably.
   */
  isCoarsePointer: boolean;
}

/**
 * Detect if device is iPad
 */
const detectIPad = (): boolean => {
  return /iPad/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Detect if device is mobile
 */
const detectMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Detect touch support
 */
const detectTouchSupport = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * `(pointer: coarse)` media-query match. True when the primary
 * pointing device is a finger / stylus with limited accuracy — iPad,
 * Android tablet, touchscreen laptop in tablet mode.
 */
const detectCoarsePointer = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(pointer: coarse)').matches;
};

/**
 * Get current orientation
 */
const getOrientation = (): 'portrait' | 'landscape' => {
  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
};

/**
 * Hook to detect device type and orientation
 */
export const useDeviceDetection = (): DeviceDetection => {
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(getOrientation);

  // These values don't change during runtime, so we compute them once.
  const [isCoarsePointer, setIsCoarsePointer] = useState<boolean>(detectCoarsePointer);
  const isIPad = detectIPad();
  const isMobile = detectMobile();
  const touchSupport = detectTouchSupport();

  useEffect(() => {
    const handleOrientationChange = () => {
      setOrientation(getOrientation());
    };

    // Some devices (iPad with Magic Keyboard, Surface) flip between
    // fine and coarse pointers on attach/detach. Listen for the
    // media-query change so we stay current.
    const coarseQuery = window.matchMedia?.('(pointer: coarse)');
    const handleCoarseChange = (e: MediaQueryListEvent) => {
      setIsCoarsePointer(e.matches);
    };

    window.addEventListener('resize', handleOrientationChange);
    coarseQuery?.addEventListener('change', handleCoarseChange);
    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      coarseQuery?.removeEventListener('change', handleCoarseChange);
    };
  }, []);

  // Mirror the coarse-pointer state to a `hts-touch` class on
  // <body>. The global CSS in `index.css` reads this class as a
  // fallback to the `@media (pointer: coarse)` rule for any
  // component that wants explicit awareness (e.g. dialogs that need
  // to know the device is touch for layout, not just for sizing).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('hts-touch', isCoarsePointer);
  }, [isCoarsePointer]);

  return { isIPad, isMobile, orientation, touchSupport, isCoarsePointer };
};
